<script setup lang="ts">
import PasswordResetLayout from '@/components/auth/PasswordResetLayout.vue';
import { useBackendFetch } from '@/fetch';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const isSubmitting = ref(false);
const completed = ref(false);
const requestError = ref('');
const showPassword = ref(false);
const showConfirmation = ref(false);

function queryValue(name: string) {
  const routeValue = route.query[name];

  if (typeof routeValue === 'string' && routeValue.length > 0) {
    return routeValue;
  }

  return new URLSearchParams(window.location.search).get(name) || '';
}

const token = computed(() => queryValue('token'));
const hasValidLink = computed(() => token.value.length > 0);
const form = reactive({
  email: queryValue('email'),
  password: '',
  password_confirmation: '',
});
const errors = reactive({ email: '', password: '', password_confirmation: '' });
const validEmailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

watch(
  () => route.query.email,
  email => {
    if (typeof email === 'string' && email.length > 0) {
      form.email = email;
    }
  },
  { immediate: true },
);

function validate() {
  errors.email = '';
  errors.password = '';
  errors.password_confirmation = '';

  if (!form.email) {
    errors.email = t('emailRequired');
  } else if (!validEmailPattern.test(form.email)) {
    errors.email = t('emailInvalid');
  }

  if (!form.password) {
    errors.password = t('PasswordReset.passwordRequired');
  } else if (form.password.length < 8) {
    errors.password = t('PasswordReset.passwordLength');
  }

  if (!form.password_confirmation) {
    errors.password_confirmation = t('PasswordReset.confirmationRequired');
  } else if (form.password !== form.password_confirmation) {
    errors.password_confirmation = t('PasswordReset.passwordMismatch');
  }

  return !errors.email && !errors.password && !errors.password_confirmation;
}

async function submit() {
  requestError.value = '';

  if (!hasValidLink.value || !validate()) {
    return;
  }

  isSubmitting.value = true;

  try {
    const { error, data } = await useBackendFetch('/password/reset')
      .post({ token: token.value, ...form })
      .json();

    if (error.value !== null) {
      const response = data.value as {
        errors?: { password?: string[] };
        message?: string;
      } | null;
      requestError.value =
        response?.errors?.password?.[0] ||
        response?.message ||
        t('PasswordReset.invalidOrExpired');
      return;
    }

    completed.value = true;
  } catch {
    requestError.value = t('PasswordReset.requestError');
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <PasswordResetLayout>
    <header class="reset-header">
      <h1 class="reset-title">{{ $t('PasswordReset.resetTitle') }}</h1>
      <p class="reset-description">
        {{ $t('PasswordReset.resetDescription') }}
      </p>
    </header>

    <div v-if="completed" class="button-stack">
      <div class="status-box success" role="status">
        {{ $t('PasswordReset.resetSuccess') }}
      </div>
      <button
        type="button"
        class="primary-button"
        @click="router.replace('/login')"
      >
        {{ $t('PasswordReset.loginWithNewPassword') }}
      </button>
    </div>

    <div v-else-if="!hasValidLink" class="button-stack">
      <div class="status-box error" role="alert">
        {{ $t('PasswordReset.invalidOrExpired') }}
      </div>
      <button
        type="button"
        class="primary-button"
        @click="router.replace('/forgot-password')"
      >
        {{ $t('PasswordReset.requestNewLink') }}
      </button>
      <button
        type="button"
        class="secondary-button"
        @click="router.replace('/login')"
      >
        {{ $t('PasswordReset.backToLogin') }}
      </button>
    </div>

    <form v-else class="reset-form" @submit.prevent="submit">
      <div class="form-group" :class="{ error: errors.email }">
        <label for="reset-email" class="form-label">{{
          $t('emailLabel')
        }}</label>
        <div class="input-wrapper">
          <svg
            class="input-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path
              d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
            />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          <input
            id="reset-email"
            v-model.trim="form.email"
            type="email"
            autocomplete="email"
            class="form-input"
          />
        </div>
        <p v-if="errors.email" class="error-message">{{ errors.email }}</p>
      </div>

      <div class="form-group" :class="{ error: errors.password }">
        <label for="new-password" class="form-label">{{
          $t('PasswordReset.newPassword')
        }}</label>
        <div class="input-wrapper">
          <svg
            class="input-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <input
            id="new-password"
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="new-password"
            class="form-input"
            :placeholder="$t('PasswordReset.passwordPlaceholder')"
          />
          <button
            type="button"
            class="toggle-password"
            :aria-label="$t('LoginPage.togglePasswordAriaLabel')"
            @click="showPassword = !showPassword"
          >
            <svg
              class="eye-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8z" />
              <circle cx="12" cy="12" r="3" />
              <line v-if="showPassword" x1="1" y1="1" x2="23" y2="23" />
            </svg>
          </button>
        </div>
        <p v-if="errors.password" class="error-message">
          {{ errors.password }}
        </p>
      </div>

      <div class="form-group" :class="{ error: errors.password_confirmation }">
        <label for="password-confirmation" class="form-label">{{
          $t('PasswordReset.confirmPassword')
        }}</label>
        <div class="input-wrapper">
          <svg
            class="input-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <input
            id="password-confirmation"
            v-model="form.password_confirmation"
            :type="showConfirmation ? 'text' : 'password'"
            autocomplete="new-password"
            class="form-input"
            :placeholder="$t('PasswordReset.confirmPasswordPlaceholder')"
          />
          <button
            type="button"
            class="toggle-password"
            :aria-label="$t('LoginPage.togglePasswordAriaLabel')"
            @click="showConfirmation = !showConfirmation"
          >
            <svg
              class="eye-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8z" />
              <circle cx="12" cy="12" r="3" />
              <line v-if="showConfirmation" x1="1" y1="1" x2="23" y2="23" />
            </svg>
          </button>
        </div>
        <p v-if="errors.password_confirmation" class="error-message">
          {{ errors.password_confirmation }}
        </p>
      </div>

      <div v-if="requestError" class="status-box error" role="alert">
        {{ requestError }}
      </div>

      <div class="button-stack">
        <button type="submit" class="primary-button" :disabled="isSubmitting">
          <span v-if="!isSubmitting">{{
            $t('PasswordReset.savePassword')
          }}</span>
          <span v-else class="spinner" aria-hidden="true"></span>
        </button>
        <button
          type="button"
          class="secondary-button"
          @click="router.replace('/login')"
        >
          {{ $t('PasswordReset.backToLogin') }}
        </button>
      </div>
    </form>
  </PasswordResetLayout>
</template>

<style scoped src="../assets/password-reset.css"></style>
